import { useState } from 'react';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/fp/isEmpty';
import { GraphQLError } from 'graphql';

import { ModalV2, ButtonV2, DatePicker } from '@bloomays-lib/ui.shared';
import type { IMissionCardDetails } from '@bloomays-lib/types.shared';
import { STOP_MISSION } from '@bloomays-lib/adapter.api-bloomer';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';

import { cancel, notify } from '../../../../helpers/toastify';
import { extractApolloErrorCode } from '@bloomays-lib/utils.shared';
import { shouldDisableEndDate, shouldDisableConfirmDate, disabledButton } from '../../../molecules/ShortenMission';
import { FieldV2, InfoV2, Row2 } from '../formFields';

export type ShortenModalProps = {
  open: boolean;
  onClose: () => void;
  mission: IMissionCardDetails;
};

/** Écourter la mission (ModalV2) — refonte V2 de `ShortenMission`, mutation `STOP_MISSION`. */
export const ShortenModal = ({ open, onClose, mission }: ShortenModalProps) => {
  const { t } = useTranslation(['extendShortenMission', 'notify', 'random', 'missionDashboard']);
  const endDate = new Date(mission?.endMission as string);

  const [pickedDate, setPickedDate] = useState<Date | null>(null);
  const [confirmedDate, setConfirmedDate] = useState<Date | null>(null);

  const [stopMission, { loading }] = useMutation(STOP_MISSION);

  const handleError = (graphqlErrors: [GraphQLError]) => {
    const graphqlError = graphqlErrors?.[0];
    const errorCode = extractApolloErrorCode(graphqlError);
    if (errorCode === 'BAD_USER_INPUT') {
      const invalidArgs = graphqlError?.extensions?.invalidArgs as Array<{ missing: string }>;
      notify(
        'error',
        t('missingFieldError', {
          ns: 'notify',
          missingField: invalidArgs ? (invalidArgs[0].missing as string) : graphqlError?.message,
        }),
      );
    } else if (errorCode === 'REFERENTIAL_INCONSISTENCY' || errorCode === 'MISSING_REFERENTIAL_DATA') {
      notify('error', graphqlError?.message || t('randomUpdateError', { ns: 'notify' }));
    } else {
      notify('error', t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const shortenMission = async (date: string) => {
    try {
      notify('info', t('updateInProgress', { ns: 'notify' }));
      const response = await stopMission({ variables: { recordId: mission.recordId, endMission: date } });
      if (response?.data.stopMission) {
        cancel();
        notify('success', t('updateSuccess', { ns: 'notify' }));
        onClose();
      } else {
        cancel();
        handleError(response.errors as [GraphQLError]);
      }
    } catch (e: any) {
      cancel();
      handleError(e.graphQLErrors as [GraphQLError]);
    }
  };

  const blocked = !mission.contractType
    ? t('pickContractType')
    : isEmpty(mission.bloomerBillingSociety)
    ? t('missingBloomerBillingSociety')
    : null;

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      icon={<EventBusyRoundedIcon sx={{ fontSize: 18 }} />}
      iconTone="rouge"
      title={t('actions.shortenMission', { ns: 'missionDashboard' })}
      width={520}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('cancel', { ns: 'random' })}
          </ButtonV2>
          <ButtonV2
            variant="danger"
            loading={loading}
            disabled={Boolean(blocked) || disabledButton(loading, pickedDate, confirmedDate)}
            startIcon={<EventBusyRoundedIcon sx={{ fontSize: 15 }} />}
            onClick={async () => {
              if (mission && pickedDate) await shortenMission(convertDateToStringFormat(pickedDate));
            }}
          >
            {`${t('validate', { ns: 'random' })} ${t('stop')}`}
          </ButtonV2>
        </>
      }
    >
      {blocked ? (
        <InfoV2 tone="amber">{blocked}</InfoV2>
      ) : (
        <>
          <InfoV2 tone="amber">{t('sadText')}</InfoV2>
          <Row2>
            <FieldV2 label={t('newDate')} required hint={`${t('newDateText')}`}>
              <DatePicker
                shouldDisableDate={shouldDisableEndDate(endDate)}
                label={t('pickDate')}
                handleChange={setPickedDate}
                value={pickedDate}
              />
            </FieldV2>
            <FieldV2 label={t('newDateConfirm')} required>
              <DatePicker
                shouldDisableDate={shouldDisableConfirmDate(pickedDate)}
                label={t('pickConfirmDate')}
                handleChange={setConfirmedDate}
                value={confirmedDate}
              />
            </FieldV2>
          </Row2>
        </>
      )}
    </ModalV2>
  );
};

export default ShortenModal;
